import {
  createClientTelemetryConfig,
  validateTelemetryConfig,
  type ClientTelemetryConfigOptions,
} from '../config';
import {
  cleanupConsoleErrorInterception,
  cleanupExceptionLogging,
  setupConsoleErrorInterception,
  setupExceptionLogging,
  setupLogging,
  shutdownLogging,
} from './logger';
import { setupMetrics, shutdownMetrics } from './metrics';
import { setupTracing, shutdownTracing } from './tracer';
import { initializeWebVitals } from './web-vitals-autoinstrumentation';

let isInitialized = false;

export function setupTelemetry(
  options?: ClientTelemetryConfigOptions,
): boolean {
  if (isInitialized) {
    console.warn('[OpenTelemetry] Already initialized');
    return true;
  }

  const config = createClientTelemetryConfig(options);

  if (!validateTelemetryConfig(config)) {
    console.error(
      '[OpenTelemetry] Invalid configuration, skipping initialization',
    );
    return false;
  }

  try {
    setupTracing(config);
    setupLogging(config);
    setupExceptionLogging();
    setupConsoleErrorInterception();
    setupMetrics(config);
    initializeWebVitals();

    isInitialized = true;

    if (config.debug) {
      console.log(
        '[OpenTelemetry] Successfully initialized (tracing, logging, metrics, web-vitals)',
      );
    }

    return true;
  } catch (error) {
    console.error('[OpenTelemetry] Failed to initialize:', error);
    return false;
  }
}

export function isTelemetryInitialized(): boolean {
  return isInitialized;
}

export async function shutdownTelemetry(): Promise<void> {
  if (!isInitialized) {
    return;
  }

  try {
    cleanupExceptionLogging();
    cleanupConsoleErrorInterception();
    await Promise.all([
      shutdownTracing(),
      shutdownLogging(),
      shutdownMetrics(),
    ]);

    isInitialized = false;
    console.log('[OpenTelemetry] Successfully shut down');
  } catch (error) {
    console.error('[OpenTelemetry] Error during shutdown:', error);
  }
}
